import { useEffect, useState } from 'react';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import getConfig from 'next/config';

import type { Environment } from 'src/constants/environment';
import { addMembershipProductToBasket, getBasket } from 'src/services/basket-api/basket-api';
import type { Basket, BasketItem } from 'src/types/Basket';
import { logger } from 'src/utils/serverLogger';

const {
  publicRuntimeConfig: { MEMBERSHIP_PRODUCT_CODE },
} = getConfig();

interface UseMembershipBasketArgs {
  currency: CurrencyCode;
  env: Environment;
}

interface UseMembershipBasketProperties {
  addMembershipToBasket: () => Promise<void>;
  basket: Basket | null;
  hasFetchBasketError: boolean;
  membershipInBasket: null | boolean;
}

const basketContainsMembershipItem = (basketItems: BasketItem[] | null) =>
  !!basketItems?.find((item) => item.product.code === MEMBERSHIP_PRODUCT_CODE);

export const useMembershipBasket = ({
  currency,
  env,
}: UseMembershipBasketArgs): UseMembershipBasketProperties => {
  const [membershipInBasket, setMembershipInBasket] = useState<null | boolean>(null);
  const [hasFetchBasketError, setHasFetchBasketError] = useState(false);
  const [basket, setBasket] = useState<Basket | null>(null);

  useEffect(() => {
    const event = new window.CustomEvent('basketUpdated');
    document.dispatchEvent(event);
  }, [basket]);

  useEffect(() => {
    const loadBasket = async () => {
      try {
        const basket = await getBasket(env, currency);
        const membershipItemInBasket = basketContainsMembershipItem(basket.items);

        setMembershipInBasket(membershipItemInBasket);
        setBasket(basket);
      } catch (error) {
        logger.error('Error fetching basket:', { error: `${String(error)}` });
        setHasFetchBasketError(true);
      }
    };

    loadBasket();
  }, [env, currency]);

  const addMembershipToBasket = async () => {
    const basket = await addMembershipProductToBasket(env, currency);

    setBasket(basket);
  };

  return {
    addMembershipToBasket,
    basket,
    membershipInBasket,
    hasFetchBasketError,
  };
};
